/**
 * Global hreflang tag injector.
 *
 * Mounted once inside AppContent so EVERY page automatically advertises
 * its bilingual counterpart to Google. Uses the single-source-of-truth
 * path map from utils/languageRoutes so any new EN/ES pair added there
 * is instantly SEO-linked — no per-page wiring needed.
 *
 * Renders nothing; manipulates document.head directly so it works without
 * react-helmet or a Next-style head abstraction.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { EN_TO_ES, ES_TO_EN } from '@/utils/languageRoutes';

const TAG_MARKER = 'data-global-hreflang';
const SITE_ORIGIN = 'https://web-techservices.com';

function applyTags(enPath, esPath) {
  // Remove any previously injected global hreflang tags so route changes
  // don't leak stale alternates.
  document.querySelectorAll(`link[${TAG_MARKER}]`).forEach((el) => el.remove());

  const make = (hreflang, href) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', SITE_ORIGIN + href);
    link.setAttribute(TAG_MARKER, '1');
    document.head.appendChild(link);
  };

  if (enPath) {
    make('en-US', enPath);
    make('x-default', enPath); // English is the default for unknown locales
  }
  if (esPath) {
    make('es', esPath);
    make('es-US', esPath);
  }
}

export default function HreflangTags() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isEs = pathname === '/es' || pathname.startsWith('/es/');
    let enPath = null;
    let esPath = null;

    if (isEs) {
      // Strip query/hash for lookup
      const cleanPath = pathname.split('?')[0].split('#')[0];
      esPath = cleanPath;
      enPath = ES_TO_EN[cleanPath] || null;
    } else {
      const cleanPath = pathname.split('?')[0].split('#')[0];
      enPath = cleanPath;
      esPath = EN_TO_ES[cleanPath] || null;
    }

    applyTags(enPath, esPath);

    return () => {
      document.querySelectorAll(`link[${TAG_MARKER}]`).forEach((el) => el.remove());
    };
  }, [pathname]);

  return null;
}
